import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ServiceBase } from 'app/core/services/service.base';
import { SessionEntry } from 'app/shared/models/session-entry.model';

const AGRONAVE_CLIENT_SOURCE = 'agronave';

interface RawLoginEvent {
  id: string;
  timestamp: number;
  clientSource: string;
  userAgent?: string;
  ipAddress?: string;
}

@Injectable({ providedIn: 'root' })
export class SessionHistoryService extends ServiceBase {
  private readonly http = inject(HttpClient);

  getSessions(limit = 5, days = 5): Observable<SessionEntry[]> {
    return this.http
      .get<
        RawLoginEvent[]
      >(`${this.UrlServiceV1}login-events?limit=${limit}&days=${days}`, this.obterAuthHeader())
      .pipe(map((events) => this.mapToSessionEntries(events)));
  }

  terminateAllSessions(): Observable<void> {
    return this.http.delete<void>(
      `${this.UrlServiceV1}auth/sessions`,
      this.obterAuthHeader(),
    );
  }

  private mapToSessionEntries(events: RawLoginEvent[]): SessionEntry[] {
    return events.map((event, index) => ({
      id: event.id,
      timestamp: event.timestamp,
      clientSource: event.clientSource as SessionEntry['clientSource'],
      userAgent: event.userAgent,
      ipAddress: event.ipAddress,
      deviceLabel: this.parseUserAgent(event.userAgent, event.clientSource),
      timeLabel: this.formatSessionTime(event.timestamp),
      isCurrent: index === 0,
    }));
  }

  private parseUserAgent(ua: string | undefined, clientSource: string): string {
    // A terminal's user agent names its HTTP library, not the device: the source says more.
    if (!ua || clientSource === AGRONAVE_CLIENT_SOURCE) {
      return this.fallbackDeviceLabel(clientSource);
    }

    const browser = this.detectBrowser(ua);
    const os = this.detectOS(ua);

    if (browser && os) return `${browser} / ${os}`;
    if (browser) return browser;
    if (os) return os;
    return this.fallbackDeviceLabel(clientSource);
  }

  private detectBrowser(ua: string): string {
    if (/Edg\//i.test(ua)) return 'Edge';
    if (/OPR\//i.test(ua)) return 'Opera';
    if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) return 'Chrome';
    if (/Firefox\//i.test(ua)) return 'Firefox';
    if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) return 'Safari';
    return '';
  }

  private detectOS(ua: string): string {
    if (/Windows NT 10/i.test(ua)) return 'Windows 10/11';
    if (/Windows NT 6\.3/i.test(ua)) return 'Windows 8.1';
    if (/Windows/i.test(ua)) return 'Windows';
    if (/iPhone/i.test(ua)) return 'iPhone / iOS';
    if (/iPad/i.test(ua)) return 'iPad / iOS';
    if (/Android/i.test(ua)) return 'Android';
    if (/Mac OS X/i.test(ua)) return 'macOS';
    if (/Linux/i.test(ua)) return 'Linux';
    return '';
  }

  private fallbackDeviceLabel(clientSource: string): string {
    const map: Record<string, string> = {
      browser: 'Navegador Web',
      mobile: 'Dispositivo Móvel',
      esp32: 'Equipamento (ESP32)',
      telemetria: 'Telemetria',
      academy: 'Tecomec Academy',
      // Product name, the same in every language.
      agronave: 'agroNave',
    };
    return map[clientSource] ?? 'Dispositivo desconhecido';
  }

  private formatSessionTime(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const timeStr = date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });

    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    if (isToday) return `Hoje, ${timeStr}`;

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    if (isYesterday) return `Ontem, ${timeStr}`;

    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
}
